// Helper function to remove error styles
function removeErrorStyle(input) {
  input.classList.remove('invalid');
  input.style.removeProperty('border-color');
  input.style.removeProperty('color');
}

// Helper function for input validation
function validateFormInput(input) {
  if (!input.required) return true;
  
  if (input.type === 'checkbox') {
    return input.checked;
  }
  
  if (input.type === 'file') {
    return input.files.length > 0;
  }
  
  if (input.tagName === 'SELECT') {
    return input.value !== '';
  }
  
  return input.value.trim() !== '';
}

// Product Form Component
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      
      // Quick exit if already defined
      if (this.initialized) return;
      this.initialized = true;
      
      // Cache DOM elements
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.hideErrors = this.dataset.hideErrors === 'true';
      
      // Error message elements
      this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessageWrapper?.querySelector('.product-form__error-message');
      this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
      
      // Pre-fetch cart components
      this.cartDrawer = document.querySelector('cart-drawer');
      this.cartNotification = document.querySelector('cart-notification');
      
      // Enable form
      const variantInput = this.form.querySelector('[name=id]');
      if (variantInput) variantInput.disabled = false;
      
      // Event binding with passive
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      
      // Quick validation checks
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      
      // Fast custom property validation
      if (!this.validateCustomProperties()) {
        this.handleErrorMessage(window.variantStrings?.customPropertyError || 'Please check required options', true);
        return;
      }
      
      // Set submitting state
      this.setSubmittingState(true);
      
      try {
        const rawCartAddUrl = (window.routes && window.routes.cart_add_url) || (typeof routes !== 'undefined' ? routes.cart_add_url : null);
        if (!rawCartAddUrl) {
          throw new Error('Missing cart_add_url');
        }
        const cartAddUrl = rawCartAddUrl.endsWith('.js') ? rawCartAddUrl : `${rawCartAddUrl}.js`;

        // Ensure variant id is included in the request.
        // Shopify forms often render `<input name="id" ... disabled>` and FormData ignores disabled inputs.
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) variantInput.disabled = false;
        const variantId = (variantInput?.value || '').toString().trim();
        if (!variantId || !/^\d+$/.test(variantId)) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Please select a variant.', true);
          return;
        }

        // Prepare form data
        const formData = new FormData(this.form);
        // Force-set variant id so the request can never "look ok" but add nothing.
        formData.set('id', variantId);
        const quantity = parseInt((formData.get('quantity') || '1').toString(), 10) || 1;
        formData.append('sections_url', window.location.pathname);
        
        // Collect sections to update
        const sections = this.collectSectionsToUpdate();
        if (sections.length > 0) {
          formData.append('sections', sections.join(','));
        }
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        // Make request
        const response = await fetch(`${cartAddUrl}`, {
          method: 'POST',
          headers: { 
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: formData,
          signal: controller.signal
        }).finally(() => clearTimeout(timeoutId));
        
        // Handle response
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        // Process response
        await this.processResponse(data);

        // Verify cart actually updated; if not, retry once with JSON payload.
        try {
          const cartCheckRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
          const cartCheck = await cartCheckRes.json();
          if (!cartCheck?.item_count) {
            const retryRes = await fetch(cartAddUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
            });
            const retryData = await retryRes.json();
            if (retryRes.ok && !retryData?.status) {
              await this.processResponse(retryData);
            } else {
              throw new Error(retryData?.description || retryData?.message || `Retry failed (${retryRes.status})`);
            }
          }
        } catch (verifyErr) {
          console.error('Cart verify/retry failed:', verifyErr);
        }
        
      } catch (error) {
        console.error('Cart add error:', error);
        this.handleError(error);
      } finally {
        this.setSubmittingState(false);
      }
    }

    setSubmittingState(isSubmitting) {
      if (isSubmitting) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');
      } else {
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        this.loadingSpinner?.classList.add('hidden');
      }
    }

    collectSectionsToUpdate() {
      const sectionSet = new Set();
      
      // Get drawer sections
      if (this.cartDrawer?.getSectionsToRender) {
        const drawerSections = this.cartDrawer.getSectionsToRender();
        drawerSections.forEach(s => sectionSet.add(s.id));
      }
      
      // Get notification sections
      if (this.cartNotification?.getSectionsToRender) {
        const notificationSections = this.cartNotification.getSectionsToRender();
        notificationSections.forEach(s => sectionSet.add(s.id));
      }

      // Fallback: if components aren't upgraded yet, still request key sections.
      // Without `sections`, Shopify returns only the added line item JSON and
      // our drawer/notification rendering will not update after an empty cart.
      if (sectionSet.size === 0) {
        const cartDrawerSectionId = document.querySelector('.shopify-section-cart-drawer')?.id?.replace('shopify-section-', '');
        const headerSectionId = document.querySelector('.shopify-section-header')?.id?.replace('shopify-section-', '');
        if (cartDrawerSectionId) sectionSet.add(cartDrawerSectionId);
        if (headerSectionId) sectionSet.add(headerSectionId);
      }
      
      return Array.from(sectionSet);
    }

    async processResponse(data) {
      // Check for errors
      const isPartialAdd = data?.description?.includes('available');
      const isError = data.status && !isPartialAdd;
      
      if (isError) {
        this.handleErrorMessage(data.message || data.description);
        this.showSoldOutState();
        return;
      }
      
      // Update UI components
      const updateTasks = [];
      
      // Update cart drawer
      if (this.cartDrawer?.renderContents) {
        updateTasks.push(Promise.resolve(this.cartDrawer.renderContents(data)));
      }
      
      // Update notification if drawer won't open
      const shouldOpenDrawer = this.cartDrawer?.classList.contains('open-after-adding');
      if (!shouldOpenDrawer && this.cartNotification?.renderContents) {
        updateTasks.push(this.updateNotification(data));
      }
      
      // Wait for updates
      await Promise.allSettled(updateTasks);

      // Emit a consistent cart update event for any custom listeners (header bubble, etc.)
      // (Some parts of this theme listen to `cart:updated` in `assets/cart.js`.)
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      
      // Handle partial add
      if (isPartialAdd) {
        this.handleErrorMessage(data.description);
      }
    }

    async updateNotification(data) {
      try {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        let itemKey = null;
        
        if (variantId) {
          // Quick cart fetch
          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          
          for (let i = 0; i < cartData.items.length; i++) {
            const item = cartData.items[i];
            if (item.variant_id?.toString() === variantId.toString()) {
              itemKey = item.key;
              break;
            }
          }
        }
        
        this.cartNotification.renderContents({
          ...data,
          key: itemKey
        });
      } catch (err) {
        this.cartNotification.renderContents(data);
      }
    }

    validateCustomProperties() {
      const productSection = this.form.closest('.main-product');
      if (!productSection) return true;
      
      const requiredInputs = productSection.querySelectorAll('.custom-options [aria-required]');
      if (requiredInputs.length === 0) return true;
      
      for (let i = 0; i < requiredInputs.length; i++) {
        const input = requiredInputs[i];
        if (input.required && !validateFormInput(input)) {
          return false;
        }
      }
      
      return true;
    }

    handleError(error) {
      let message = 'An error occurred while adding to the cart.';
      
      if (error.name === 'AbortError') {
        message = 'Request timeout. Please try again.';
      } else if (error.message.includes('HTTP')) {
        message = 'Network error. Please check your connection.';
      }
      
      this.handleErrorMessage(message);
      this.showSoldOutState();
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors || !this.errorMessageWrapper) return;
      
      if (errorMessage) {
        this.errorMessageWrapper.hidden = false;
        if (this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      } else {
        this.errorMessageWrapper.hidden = true;
      }
    }

    showSoldOutState() {
      const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
      if (soldOutMessage) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        const buttonText = this.submitButton.querySelector('span');
        if (buttonText) buttonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      }
    }
  });
}

// Custom Options Component
if (!customElements.get('custom-options')) {
  customElements.define('custom-options', class CustomOptions extends HTMLElement {
    constructor() {
      super();
      
      // Cache elements
      this.requiredInputs = this.querySelectorAll('[aria-required]');
      this.output = this.querySelector('[data-selected-custom-option]');
      this.optionType = this.dataset.type;
      
      // Bind handlers
      this.handleChange = this.handleChange.bind(this);
      this.handleFileChange = this.handleFileChange.bind(this);
      this.handleDragOver = this.handleDragOver.bind(this);
      this.handleDragLeave = this.handleDragLeave.bind(this);
      this.handleDrop = this.handleDrop.bind(this);
    }

    connectedCallback() {
      // Setup event listeners
      this.addEventListener('change', this.handleChange);
      
      // Setup specific features
      if (this.optionType === 'color-picker') {
        this.setupColorPicker();
      } else if (this.optionType === 'image-upload') {
        this.setupFileUpload();
      }
      
      // Validate existing required inputs
      this.validateRequiredInputs();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      
      // Cleanup file upload events
      if (this.optionType === 'image-upload') {
        this.cleanupFileUploadEvents();
      }
    }

    handleChange(event) {
      const target = event.target;
      
      // Handle validation
      if (target.hasAttribute('aria-required')) {
        this.validateInput(target);
      }
      
      // Update output display
      if (this.output && this.isOutputRelevant(target)) {
        this.updateOutput();
      }
    }

    isOutputRelevant(element) {
      const relevantTypes = ['radio', 'checkbox'];
      return relevantTypes.includes(element.type) || element.tagName === 'SELECT';
    }

    updateOutput() {
      const selectedValues = [];
      const relevantInputs = this.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked, select');
      
      for (let i = 0; i < relevantInputs.length; i++) {
        const input = relevantInputs[i];
        
        if (input.tagName === 'SELECT') {
          const option = input.options[input.selectedIndex];
          if (option?.value) {
            selectedValues.push(option.textContent.trim());
          }
        } else if (input.checked) {
          selectedValues.push(input.value.trim());
        }
      }
      
      this.output.textContent = selectedValues.length > 0 ? '\u2014 ' + selectedValues.join(', ') : '';
    }

    setupColorPicker() {
      const colorInput = this.querySelector('input[type="color"]');
      if (!colorInput) return;
      
      colorInput.addEventListener('input', (event) => {
        const value = event.target.value;
        const colorLabel = this.querySelector('.selected-color-label');
        const colorSwatch = this.querySelector('.color__swatch--color');
        
        if (colorLabel) colorLabel.textContent = value;
        if (colorSwatch) {
          colorSwatch.style.setProperty('--swatch-background', value);
          colorSwatch.style.setProperty('--swatch-background-color', value);
        }
      });
    }

    setupFileUpload() {
      this.fileInput = this.querySelector('.drop-zone__input');
      this.dropZone = this.querySelector('.drop-zone');
      this.dropZoneWrap = this.querySelector('.drop-zone-wrap');
      
      if (!this.fileInput || !this.dropZone) return;
      
      // Add drag and drop events
      this.dropZone.addEventListener('dragover', this.handleDragOver);
      this.dropZone.addEventListener('dragleave', this.handleDragLeave);
      this.dropZone.addEventListener('drop', this.handleDrop);
      this.fileInput.addEventListener('change', this.handleFileChange);
    }

    cleanupFileUploadEvents() {
      if (this.dropZone) {
        this.dropZone.removeEventListener('dragover', this.handleDragOver);
        this.dropZone.removeEventListener('dragleave', this.handleDragLeave);
        this.dropZone.removeEventListener('drop', this.handleDrop);
      }
      if (this.fileInput) {
        this.fileInput.removeEventListener('change', this.handleFileChange);
      }
    }

    handleDragOver(event) {
      event.preventDefault();
      this.dropZone?.classList.add('drop-zone--over');
    }

    handleDragLeave(event) {
      event.preventDefault();
      this.dropZone?.classList.remove('drop-zone--over');
    }

    handleDrop(event) {
      event.preventDefault();
      this.dropZone?.classList.remove('drop-zone--over');
      
      if (event.dataTransfer?.files.length && this.fileInput) {
        const file = event.dataTransfer.files[0];
        if (this.validateFile(file)) {
          this.fileInput.files = event.dataTransfer.files;
          this.previewFile(file);
        }
      }
    }

    handleFileChange(event) {
      if (!this.fileInput?.files.length) return;
      
      const file = this.fileInput.files[0];
      if (this.validateFile(file)) {
        this.previewFile(file);
      }
    }

    validateFile(file) {
      const maxSizeMB = 5;
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(4);
      
      if (fileSizeMB > maxSizeMB) {
        this.showFileError(window.variantStrings?.fileSizeError || 'File size must be less than 5MB');
        this.fileInput.value = '';
        return false;
      }
      
      this.hideFileError();
      return true;
    }

    showFileError(message) {
      if (!this.dropZoneWrap) return;
      
      this.dropZoneWrap.classList.add('drop-zone-wrap--error');
      const errorElement = this.dropZoneWrap.querySelector('.input-error-message');
      if (errorElement) {
        errorElement.classList.remove('visually-hidden');
        errorElement.textContent = message;
      }
    }

    hideFileError() {
      if (!this.dropZoneWrap) return;
      
      this.dropZoneWrap.classList.remove('drop-zone-wrap--error');
      const errorElement = this.dropZoneWrap.querySelector('.input-error-message');
      if (errorElement) {
        errorElement.classList.add('visually-hidden');
      }
    }

    previewFile(file) {
      const reader = new FileReader();
      
      reader.onload = () => {
        this.createThumbnail(file, reader.result);
      };
      
      reader.readAsDataURL(file);
    }

    createThumbnail(file, dataUrl) {
      // Remove existing thumbnail
      const existingThumbnail = this.dropZoneWrap?.querySelector('.drop-zone__thumb');
      if (existingThumbnail) {
        existingThumbnail.remove();
      }
      
      // Create new thumbnail
      const thumbnail = document.createElement('div');
      thumbnail.className = 'drop-zone__thumb';
      thumbnail.setAttribute('data-file-name', file.name);
      
      // Create file info
      const fileInfo = this.createFileInfo(file);
      thumbnail.appendChild(fileInfo);
      
      // Create preview
      const preview = this.createPreview(file, dataUrl);
      thumbnail.appendChild(preview);
      
      // Add remove button
      const removeBtn = this.createRemoveButton();
      thumbnail.appendChild(removeBtn);
      
      // Add to DOM
      if (this.dropZoneWrap) {
        this.dropZoneWrap.appendChild(thumbnail);
        this.dropZoneWrap.classList.add('drop-zone-wrap--inactive');
      }
    }

    createFileInfo(file) {
      const fileInfo = document.createElement('div');
      fileInfo.className = 'dd-file-info';
      
      const textDiv = document.createElement('div');
      textDiv.className = 'dd-file-info__text flex--column';
      
      const title = document.createElement('span');
      title.className = 'dd-file-info__title';
      title.textContent = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      
      const type = document.createElement('span');
      type.className = 'dd-file-info__type caption-font';
      type.textContent = `${this.formatFileSize(file.size)}, ${this.formatFileType(file.type)}`;
      
      textDiv.appendChild(title);
      textDiv.appendChild(type);
      fileInfo.appendChild(textDiv);
      
      return fileInfo;
    }

    createPreview(file, dataUrl) {
      const preview = document.createElement('div');
      preview.className = 'dd-thumbnail';
      
      if (/\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
        preview.style.backgroundImage = `url("${dataUrl}")`;
        preview.style.width = '60px';
        preview.style.height = '60px';
        preview.style.backgroundSize = 'cover';
        preview.style.backgroundPosition = 'center';
      } else {
        const icon = this.getFileIcon(file);
        if (icon) {
          preview.appendChild(icon);
        }
      }
      
      return preview;
    }

    createRemoveButton() {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'button link-button button--secondary file-upload__remove';
      removeBtn.textContent = 'Remove';
      
      removeBtn.addEventListener('click', () => {
        const thumbnail = this.dropZoneWrap?.querySelector('.drop-zone__thumb');
        if (thumbnail) {
          thumbnail.remove();
        }
        if (this.fileInput) {
          this.fileInput.value = '';
        }
        if (this.dropZoneWrap) {
          this.dropZoneWrap.classList.remove('drop-zone-wrap--inactive');
        }
      });
      
      return removeBtn;
    }

    getFileIcon(file) {
      const iconMap = {
        'pdf': 'icon--file',
        'excel': 'icon--file',
        'word': 'icon--file',
        'video': 'icon--video',
        'audio': 'icon--audio',
        'image': 'icon--image'
      };
      
      let iconType = 'file';
      
      if (file.type.startsWith('image/')) {
        iconType = 'image';
      } else if (file.type.startsWith('video/')) {
        iconType = 'video';
      } else if (file.type.startsWith('audio/')) {
        iconType = 'audio';
      } else if (file.type.includes('pdf')) {
        iconType = 'pdf';
      } else if (file.type.includes('excel') || file.type.includes('spreadsheet')) {
        iconType = 'excel';
      } else if (file.type.includes('word') || file.type.includes('document')) {
        iconType = 'word';
      }
      
      const iconClass = iconMap[iconType] || 'icon--file';
      const iconElement = this.querySelector(`.${iconClass}`);
      
      return iconElement ? iconElement.cloneNode(true) : null;
    }

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    formatFileType(mimeType) {
      const typeMap = {
        'application/pdf': 'PDF',
        'application/vnd.ms-excel': 'Excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
        'application/msword': 'Word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
        'text/plain': 'Text',
        'image/jpeg': 'JPEG Image',
        'image/png': 'PNG Image',
        'image/gif': 'GIF Image',
        'video/mp4': 'MP4 Video',
        'audio/mpeg': 'MP3 Audio'
      };
      
      return typeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'File';
    }

    validateRequiredInputs() {
      for (let i = 0; i < this.requiredInputs.length; i++) {
        this.validateInput(this.requiredInputs[i]);
      }
    }

    validateInput(input) {
      const productSection = this.closest('.main-product');
      if (!productSection) return;
      
      const productForm = productSection.querySelector('product-form');
      const dynamicCheckout = productSection.querySelector('.shopify-payment-button');
      
      if (!input.value || (input.type === 'checkbox' && !input.checked)) {
        dynamicCheckout?.classList.add('shopify-payment-button--disable');
      } else if (input.classList.contains('invalid')) {
        input.classList.remove('invalid');
        
        // Check if all errors are cleared
        const hasErrors = productSection.querySelectorAll('.invalid').length > 0;
        if (!hasErrors) {
          dynamicCheckout?.classList.remove('shopify-payment-button--disable');
          productForm?.handleErrorMessage();
        }
      }
    }
  });
}

// Product Checkbox Group Component
if (!customElements.get('product-checkbox-group')) {
  customElements.define('product-checkbox-group', class ProductCheckboxGroup extends HTMLElement {
    constructor() {
      super();
      
      this.inputs = this.querySelectorAll('input[type="checkbox"]');
      this.mainInput = this.querySelector('input[type="hidden"]');
      this.minLimit = parseInt(this.dataset.min) || 0;
      this.maxLimit = parseInt(this.dataset.max) || 0;
      
      // Bind handler
      this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
      
      // Initial setup
      this.updateMainInput();
      this.updateInputStates();
    }

    connectedCallback() {
      this.addEventListener('change', this.handleCheckboxChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleCheckboxChange);
    }

    handleCheckboxChange(event) {
      if (event.target.type !== 'checkbox') return;
      
      this.updateMainInput();
      this.updateInputStates();
      this.validateCheckboxGroup();
    }

    updateMainInput() {
      const checkedValues = [];
      
      for (let i = 0; i < this.inputs.length; i++) {
        if (this.inputs[i].checked) {
          checkedValues.push(this.inputs[i].value);
        }
      }
      
      this.mainInput.value = checkedValues.join(',');
    }

    updateInputStates() {
      const checkedCount = this.getCheckedCount();
      
      // Handle max limit
      if (this.maxLimit > 0 && checkedCount >= this.maxLimit) {
        for (let i = 0; i < this.inputs.length; i++) {
          if (!this.inputs[i].checked) {
            this.inputs[i].disabled = true;
          }
        }
      } else {
        for (let i = 0; i < this.inputs.length; i++) {
          this.inputs[i].disabled = false;
        }
      }
    }

    getCheckedCount() {
      let count = 0;
      for (let i = 0; i < this.inputs.length; i++) {
        if (this.inputs[i].checked) count++;
      }
      return count;
    }

    validateCheckboxGroup() {
      const checkedCount = this.getCheckedCount();
      const productSection = this.closest('.main-product');
      const dynamicCheckout = productSection?.querySelector('.shopify-payment-button');
      
      if (!dynamicCheckout) return;
      
      // Check min limit
      if (this.minLimit > 0) {
        if (checkedCount >= this.minLimit) {
          this.clearErrors();
          
          // Check other required inputs
          if (this.areOtherInputsValid(productSection)) {
            dynamicCheckout.classList.remove('shopify-payment-button--disable');
          }
        } else {
          dynamicCheckout.classList.add('shopify-payment-button--disable');
        }
      }
    }

    clearErrors() {
      const errorElement = this.querySelector('.invalid');
      if (errorElement) {
        errorElement.classList.remove('invalid');
      }
      
      const parentCustomOptions = this.closest('.custom-options');
      const errorMessage = parentCustomOptions?.querySelector('.input-error-message');
      if (errorMessage) {
        errorMessage.classList.add('visually-hidden');
      }
    }

    areOtherInputsValid(productSection) {
      const otherRequiredInputs = productSection.querySelectorAll('[aria-required]:not(.checkbox-group__input)');
      
      for (let i = 0; i < otherRequiredInputs.length; i++) {
        const input = otherRequiredInputs[i];
        if (!validateFormInput(input)) {
          return false;
        }
      }
      
      return true;
    }
  });
}

// Initialize components when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Auto-initialize any existing product forms
  document.querySelectorAll('product-form').forEach(form => {
    if (!form.initialized) {
      customElements.upgrade(form);
    }
  });
  
  // Auto-initialize custom options
  document.querySelectorAll('custom-options').forEach(options => {
    if (!options.initialized) {
      customElements.upgrade(options);
    }
  });
  
  // Auto-initialize checkbox groups
  document.querySelectorAll('product-checkbox-group').forEach(group => {
    if (!group.initialized) {
      customElements.upgrade(group);
    }
  });
});

// Fallback for browsers without custom elements support
if (!window.customElements) {
  console.warn('Custom Elements not supported. Falling back to basic form handling.');
  
  // Basic form submission handling
  document.addEventListener('submit', function(event) {
    const form = event.target;
    if (form.closest('product-form')) {
      event.preventDefault();
      
      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton.getAttribute('aria-disabled') === 'true') return;
      
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      
      // Basic fetch
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || (typeof routes !== 'undefined' ? routes.cart_add_url : null);
      if (!cartAddUrl) {
        alert('Unable to add to cart. Please refresh the page.');
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
        return;
      }

      fetch(`${cartAddUrl}`, {
        method: 'POST',
        body: new FormData(form)
      })
      .then(response => response.json())
      .then(data => {
        if (data.status) {
          alert(data.message || 'Error adding to cart');
        } else {
          // Basic cart update
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.renderContents(data);
          }
        }
      })
      .catch(error => {
        console.error('Error:', error);
        alert('Error adding to cart');
      })
      .finally(() => {
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
      });
    }
  });
}
